import { BuyerLink, UserCreatedContext } from '@choco-admin/types';
import {
  CountryCode,
  getCountryCodeFromPhoneNumber,
  DEFAULT_LOCALE,
} from '@choco-admin/utils';
import { Divider } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ProfilePicture } from '../../../../components/FormComponents';
import { FormFooter } from '../../../../components/FormFooter';
import { IsPuppyIndicator } from '../../../../components/IsPuppyIndicator';
import { SsoRestrictionProvider } from '../../contexts';
import { isSsoLoginMethod } from '../../utils/loginMethodDisplay';
import { BuyerSelectOption } from '../BuyerLinking/types';
import { OrderPreferencesField } from '../OrderPreferences';
import { SupplierSelectOption } from '../SupplierLinking/utils';

import {
  AdminActionsSection,
  BasicInformationSection,
  CognitoStateSection,
  CreateUserLinkingSection,
  LoginMethodSection,
  TwilioStateSection,
  UserLinkingSection,
  UserTypeSection,
} from './components';
import {
  useUserFormValidation,
  useProfilePicture,
  type UserFormData,
  type UserFormUser,
} from './hooks';
import { toChangedFields, UserFormChangedFields } from './utils/changedFields';

interface UserFormProps {
  initialData?: Partial<UserFormData>;
  /**
   * `changedFields` reports which fields differ from the values the form loaded
   * with, so an edit can send a payload limited to what the admin touched.
   * `pendingProfileImage` is the edited canvas from the picture editor when a
   * new user has no `uploadUrl` yet to upload to immediately — the caller
   * must upload it once the created user's `uploadUrl` is known.
   */
  onSubmit: (
    data: UserFormData,
    changedFields: UserFormChangedFields,
    pendingProfileImage?: HTMLCanvasElement | null
  ) => Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
  isEditMode?: boolean;
  user?: UserFormUser | null; // For ProfilePicture backend integration and supplier data
  userBuyers?: BuyerLink[];
  onCancel?: () => void;
  onSignInAsUser?: () => void;
  disabled?: boolean; // Make form read-only
}

export const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Save User',
  isEditMode = false,
  user = null,
  userBuyers = [],
  onCancel,
  onSignInAsUser,
  disabled = false,
}) => {
  // Country code state management - extract from phone number if available
  const getInitialCountryCode = (): CountryCode => {
    if (initialData?.countryCode) {
      return initialData.countryCode as CountryCode;
    }
    if (initialData?.phone) {
      return getCountryCodeFromPhoneNumber(initialData.phone) || 'US';
    }
    return 'US';
  };

  const [countryCode, setCountryCode] = useState<CountryCode>(
    getInitialCountryCode()
  );

  // Use validation hook
  const {
    validationSchema,
    isLoginEmailMethodSelected,
    isLoginPhoneMethodSelected,
    getInputSubLabel,
  } = useUserFormValidation();

  // Holds the edited canvas when the user has no `uploadUrl` yet to upload
  // to immediately (create mode) — the caller uploads it after creation.
  const [pendingProfileImage, setPendingProfileImage] =
    useState<HTMLCanvasElement | null>(null);

  const profilePictureHook = useProfilePicture({
    user,
    onImageChange: setPendingProfileImage,
  });

  // Helper function to get initial country code from phone
  const getCountryCodeFromPhone = (phone?: string | null): CountryCode => {
    if (phone && phone.startsWith('+')) {
      const detected = getCountryCodeFromPhoneNumber(phone);
      return detected || 'US';
    }
    return 'US';
  };

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      locale: initialData?.locale || DEFAULT_LOCALE,
      loginMethod: initialData?.loginMethod || 'phone',
      isTest: initialData?.isTest || false,
      supplier: initialData?.supplier || false,
      linkedBuyers: initialData?.linkedBuyers || [],
      supplierId: initialData?.supplierId || '',
      countryCode: (initialData?.countryCode || 'US') as CountryCode,
      supportLogin: initialData?.supportLogin || false,
      whatsAppCountryCode: getCountryCodeFromPhone(
        initialData?.orderPreferences?.whatsApp
      ),
      smsCountryCode: getCountryCodeFromPhone(
        initialData?.orderPreferences?.sms
      ),
      orderPreferences: {
        pdf: initialData?.orderPreferences?.pdf || false,
        xlsx: initialData?.orderPreferences?.xlsx || false,
        sms: initialData?.orderPreferences?.sms || null,
        whatsApp: initialData?.orderPreferences?.whatsApp || null,
      },
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    watch,
    setValue,
    clearErrors,
  } = form;
  const selectedLoginMethod = watch('loginMethod');
  const emailMethodSelected = isLoginEmailMethodSelected(selectedLoginMethod);
  const phoneMethodSelected = isLoginPhoneMethodSelected(selectedLoginMethod);
  const watchSupplier = watch('supplier');

  // Edit mode links and unlinks in place, outside the form values, so the
  // live link state is kept here for both the linking and user type sections.
  const [linkedBuyerCount, setLinkedBuyerCount] = useState(userBuyers.length);
  const [linkedDistributorId, setLinkedDistributorId] = useState<string | null>(
    user?.supplierId || null
  );
  const selectedBuyerCount = (watch('linkedBuyers') || []).length;
  const selectedDistributorId = watch('supplierId');

  // SSO users' identity details are owned by their identity provider, so once
  // created, the identity-bearing fields (basic info, account type, profile
  // picture, linked buyers/distributors) are read-only. Phone number and order
  // preferences stay editable. A hard `disabled` (e.g. deleted user) still
  // locks everything. This restriction only applies in edit mode — at
  // creation time the admin must still supply the identity fields (e.g. the
  // email used as `loginEmail`). The flag is shared with the sections through
  // SsoRestrictionProvider rather than prop-drilled; each section combines it
  // with its own `disabled`.
  const isSsoUser = isEditMode && isSsoLoginMethod(selectedLoginMethod);

  // Support login issues a daily code in place of the SMS code, so it cannot be
  // granted to an account whose credentials live with an external identity
  // provider. `createdContext` is the authoritative marker: the backend derives
  // `loginMethod` from which contact fields are set, so a federated account
  // commonly reports `email` rather than `federated`. `isSsoUser` is kept as a
  // second signal for accounts predating `createdContext`.
  const isFederatedUser =
    user?.createdContext === UserCreatedContext.FEDERATED || isSsoUser;

  // Order preferences control how order confirmations are delivered, and only
  // distributors receive those — buyer users never do.
  const showOrderPreferences = !!watchSupplier;

  const handleFormSubmit = async (data: UserFormData) => {
    // The form seeds order preferences from the loaded user, so a hidden
    // section must drop them rather than submit values nobody could see.
    await onSubmit(
      showOrderPreferences ? data : { ...data, orderPreferences: undefined },
      toChangedFields(dirtyFields),
      pendingProfileImage
    );
  };

  const handleSave = () => {
    handleSubmit(handleFormSubmit)();
  };

  return (
    <SsoRestrictionProvider isSsoUser={isSsoUser}>
      {user?.isPuppy && <IsPuppyIndicator />}
      <form className="space-y-6">
        {isSsoUser && (
          <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
            <p className="text-sm text-warning-800">
              <strong>Single sign-on user:</strong> This user&apos;s profile
              details are managed by their identity provider and are read-only
              here.{' '}
              {showOrderPreferences
                ? 'Name, phone number, and order preferences remain editable.'
                : 'Name and phone number remain editable.'}
            </p>
          </div>
        )}

        <LoginMethodSection
          control={control}
          errors={errors}
          isEditMode={isEditMode}
          disabled={disabled}
        />

        <BasicInformationSection
          control={control}
          errors={errors}
          countryCode={countryCode}
          onChangeCountryCode={setCountryCode}
          setValue={setValue}
          isLoginEmailMethodSelected={emailMethodSelected}
          isLoginPhoneMethodSelected={phoneMethodSelected}
          getInputSubLabel={getInputSubLabel}
          disabled={disabled}
        />

        <Divider />

        {/* User Linking */}
        {isEditMode && user ? (
          <UserLinkingSection
            user={user}
            userBuyers={userBuyers}
            disabled={disabled}
            linkedBuyerCount={linkedBuyerCount}
            linkedDistributorId={linkedDistributorId}
            onLinkedBuyersChange={setLinkedBuyerCount}
            onLinkedDistributorChange={setLinkedDistributorId}
          />
        ) : (
          <CreateUserLinkingSection
            control={control}
            watch={watch}
            onBuyerSelect={(buyers: BuyerSelectOption[]) => {
              setValue('linkedBuyers', buyers);
            }}
            onSupplierSelect={(supplier: SupplierSelectOption | null) => {
              setValue('supplier', !!supplier);
              setValue('supplierId', supplier?.value || '');
            }}
            selectedBuyers={watch('linkedBuyers') || []}
            selectedSupplier={
              watch('supplierId') && watch('supplierId') !== ''
                ? {
                    value: watch('supplierId') || '',
                    label: watch('supplierName') || 'Selected Distributor',
                  }
                : null
            }
            disabled={disabled}
          />
        )}

        <UserTypeSection
          control={control}
          errors={errors}
          watch={watch}
          isEditMode={isEditMode}
          disabled={disabled}
          isFederatedUser={isFederatedUser}
          linkedBuyerCount={isEditMode ? linkedBuyerCount : selectedBuyerCount}
          hasLinkedDistributor={
            isEditMode ? !!linkedDistributorId : !!selectedDistributorId
          }
        />

        {/* Profile Picture */}
        <div className="space-y-4">
          <ProfilePicture
            onImageSave={profilePictureHook.handleImageSave}
            user={
              user
                ? {
                    id: user.id,
                    name: user.name || undefined,
                    profileImage: user.profileImage
                      ? {
                          url: user.profileImage.url || undefined,
                          mediumSquare:
                            user.profileImage.mediumSquare || undefined,
                        }
                      : undefined,
                  }
                : undefined
            }
            isDisabled={
              isSubmitting ||
              profilePictureHook.isUploading ||
              disabled ||
              isSsoUser
            }
            data-testid="profile-picture-field"
          />
        </div>

        {/* Order Preferences — editable for SSO users (portal-managed). */}
        {showOrderPreferences && (
          <OrderPreferencesField
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
            disabled={disabled}
          />
        )}

        {!watchSupplier && (
          <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
            <p className="text-sm text-warning-800">
              <strong>Note:</strong> Users must be either test users,
              distributors, or linked to buyers to be valid.
            </p>
          </div>
        )}

        {/* State Components - only show in edit mode */}
        {isEditMode && user?.id && (
          <>
            <CognitoStateSection userId={user.id} />
            <TwilioStateSection userId={user.id} />
          </>
        )}

        {/* Admin Actions */}
        <AdminActionsSection
          user={user}
          isEditMode={isEditMode}
          onSignInAsUser={onSignInAsUser}
        />

        <div className="pt-4">
          <FormFooter
            saveText={submitLabel}
            cancelText="Cancel"
            onSave={handleSave}
            onCancel={onCancel || (() => window.history.back())}
            isSaving={isSubmitting}
            isSaveDisabled={!isDirty || disabled}
            isCancelDisabled={disabled}
          />
        </div>
      </form>
    </SsoRestrictionProvider>
  );
};
